import { useState } from "react";
import Student from "types/Student";
import { Avatar } from "./ui/Avatar";
import fetchWithCredentials from "apiClient";
import { Check, X } from "lucide-react";
import { AcceptPendingStudentDialog } from "./AcceptPendingStudentDialog";
import { GradeLevelGroup } from "types/Student";

interface PendingStudentsListProps {
  students: Student[];
  selectedStudentId: number | null;
  onSelectStudent: (studentId: number) => void;
  onStudentAction: () => void;
  gradeLevelGroups: GradeLevelGroup[];
}

export function PendingStudentsList({
  students,
  selectedStudentId,
  onSelectStudent,
  onStudentAction,
  gradeLevelGroups,
}: PendingStudentsListProps) {
  const [loadingStates, setLoadingStates] = useState<Record<number, string>>(
    {}
  );
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [studentToAccept, setStudentToAccept] = useState<Student | null>(null);

  const handleAccept = (student: Student) => {
    setStudentToAccept(student);
    setAcceptDialogOpen(true);
  };

  const handleAcceptSuccess = () => {
    onStudentAction();
    setAcceptDialogOpen(false);
    setStudentToAccept(null);
  };

  const handleAcceptClose = () => {
    setAcceptDialogOpen(false);
    setStudentToAccept(null);
  };

  const handleReject = async (studentId: number) => {
    setLoadingStates((prev) => ({ ...prev, [studentId]: "rejecting" }));
    try {
      await fetchWithCredentials(
        `/api/teacher/pending-students/${studentId}/reject/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      onStudentAction();
    } catch (error) {
      console.error("Error rejecting student:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [studentId]: "" }));
    }
  };

  if (students.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xs lg:text-sm font-bold uppercase text-gray-500">
          Pending Students
        </h2>
      </div>
      <ul className="flex flex-col space-y-1 text-xs lg:text-sm list-none p-0">
        {students.map((student) => (
          <li
            key={student.id}
            className={`student-list-item flex items-center justify-between px-2 py-1 rounded cursor-pointer lg:hover:bg-gray-100 group ${
              selectedStudentId === student.id
                ? "bg-blue-100 text-blue-700 font-semibold"
                : ""
            }`}
          >
            <div
              className="flex items-center space-x-2 flex-1"
              onClick={() => onSelectStudent(student.id)}
              onTouchEnd={(e) => {
                // Prevent default to avoid double-tap zoom and ensure click fires
                e.preventDefault();
                onSelectStudent(student.id);
              }}
            >
              <Avatar />
              <span className="truncate">{student.name}</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleAccept(student)}
                disabled={loadingStates[student.id] === "accepting"}
                className="inline-flex items-center justify-center w-5 h-5 lg:w-6 lg:h-6 rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                aria-label="Accept Student"
                title="Accept student and add to regular students"
              >
                {loadingStates[student.id] === "accepting" ? (
                  <div className="w-3 h-3 border border-green-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Check className="w-3 h-3" />
                )}
              </button>
              <button
                onClick={() => handleReject(student.id)}
                disabled={loadingStates[student.id] === "rejecting"}
                className="inline-flex items-center justify-center w-6 h-6 rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                aria-label="Reject Student"
                title="Reject student and remove from this section"
              >
                {loadingStates[student.id] === "rejecting" ? (
                  <div className="w-3 h-3 border border-red-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <X className="w-3 h-3" />
                )}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {acceptDialogOpen && studentToAccept && (
        <AcceptPendingStudentDialog
          isOpen={acceptDialogOpen}
          onClose={handleAcceptClose}
          studentId={studentToAccept.id}
          studentName={studentToAccept.name}
          gradeLevelGroups={gradeLevelGroups}
          onSuccess={handleAcceptSuccess}
        />
      )}
    </div>
  );
}
